import React, {useEffect, useState} from "react";
import {Badge, Button, Card, CardBody, Col, Row} from "reactstrap";
import LoadingSpinner from "../../../../shared/components/LoadingSpinner";
import PropTypes from "prop-types";
import CredentialTableWithPagination from "./components/CredentialTableWithPagination";
import {useCredentialExposureProjectResultsCount} from "../../../../queries/IdentityMonitorResults";
import {useSetAllCredentialsExpSolved} from "../../../../mutations/SetAllCredentialsExpSolved";
import toast from "react-hot-toast";
import {getErrorMessageFromResponse} from "../../../../shared/helpers";
import {useSetCredentialsExpSolved} from "../../../../mutations/SetCredentialsExpSolved";
import RegisteredCheckBoxField from "../../../../shared/components/form/RegisteredCheckBoxField";
import {useForm} from "react-hook-form";
import {download_file} from "../../../../shared/helpers/download_file";
import {apiUrlInternal} from "../../../../config/api";
import {formatDateUTCtoYearMonthDayTimeFile} from "../../../../shared/helpers/date";
import moment from "moment";
import {useCookieAccessToken} from "../../../../queries";


const CredentialExposure = ({project_uid, project_name}) => {
  const {
    data,
    isIdle,
    isLoading,
    isError,
    isFetching
  } = useCredentialExposureProjectResultsCount(
    project_uid
  );

  const mutationSetAllCredentialsExpSolved = useSetAllCredentialsExpSolved();
  const mutationSetCredentialsExpSolved = useSetCredentialsExpSolved();
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [btnSelectDisabled, setBtnSelectDisabled] = useState(true);
  const {register, setValue, watch} = useForm();
  const [uidsSelected, setUidsSelected] = useState([]);
  const [count, setCount] = useState(0);
  const {data: accessToken} = useCookieAccessToken();

  const unsetSelection = () => {
    for (let i = 0; i < count; ++i) {
      setValue(`cred.${i}`, false)
    }
  }

  useEffect(() => {
    setBtnSelectDisabled(uidsSelected.length === 0);
  }, [uidsSelected])

  useEffect(() => {
    if (!mutationSetAllCredentialsExpSolved.isIdle) {
      toast.dismiss();

      if (mutationSetAllCredentialsExpSolved.isLoading) {
        toast.loading('Marking all credentials as solved');
      } else {
        setBtnDisabled(false);
        if (mutationSetAllCredentialsExpSolved.isSuccess) {
          toast.success('Credentials marked as solved');
          unsetSelection();
        } else if (mutationSetAllCredentialsExpSolved.isError) {
          toast.error(getErrorMessageFromResponse(mutationSetAllCredentialsExpSolved));
          mutationSetAllCredentialsExpSolved.reset();
        }
      }
    }
  }, [
    mutationSetAllCredentialsExpSolved.isLoading,
    mutationSetAllCredentialsExpSolved.isIdle,
    mutationSetAllCredentialsExpSolved.isSuccess,
    mutationSetAllCredentialsExpSolved.isError,
    getErrorMessageFromResponse(mutationSetAllCredentialsExpSolved),
  ]);

  useEffect(() => {
    if (!mutationSetCredentialsExpSolved.isIdle) {
      toast.dismiss();

      if (mutationSetCredentialsExpSolved.isLoading) {
        toast.loading('Marking  credentials as solved');
      } else {
        setBtnDisabled(false);
        if (mutationSetCredentialsExpSolved.isSuccess) {
          toast.success('Credentials marked as solved');
          unsetSelection();
        } else if (mutationSetCredentialsExpSolved.isError) {
          toast.error(getErrorMessageFromResponse(mutationSetCredentialsExpSolved));
          mutationSetCredentialsExpSolved.reset();
        }
      }
    }
  }, [
    mutationSetCredentialsExpSolved.isLoading,
    mutationSetCredentialsExpSolved.isIdle,
    mutationSetCredentialsExpSolved.isSuccess,
    mutationSetCredentialsExpSolved.isError,
    getErrorMessageFromResponse(mutationSetCredentialsExpSolved),
  ]);

  useEffect(() => {
    if (!isIdle && !isLoading && !isFetching && data?.data) {
      setCount(data.data.count);
    }

  }, [isIdle, isLoading, isFetching, data])

  if (isIdle || isLoading || isFetching) {
    return <LoadingSpinner/>
  }

  if (isError) {
    return <Card><CardBody><h4>Unable to load the content</h4></CardBody></Card>;
  }

  if (count === 0) {
    return (
      <Card>
        <CardBody>
          <Row>
            <Col>
              <Badge color={'success'} className={'w-100 outline badge-lg'}>NO CREDENTIALS FOUND</Badge>
            </Col>
          </Row>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardBody>
        <Row>
          <Col md={12}>
            <Badge color={'danger'} className={'w-100 outline badge-lg'}>
              {
                count === 1 ? `THERE IS ${count} COMPROMISED CREDENTIAL` :
                  `THERE ARE ${count} COMPROMISED CREDENTIALS`
              }
            </Badge>
          </Col>
        </Row>

        {/*<Row className={'mt-3'}>*/}
        {/*  <Col md={12} lg={6}>*/}
        {/*    <CredentialPasswordWordCloud credentials={creds} />*/}
        {/*  </Col>*/}

        {/*  <Col md={12} lg={6}>*/}
        {/*    <CredentialSourcePieChart credentials={creds} />*/}
        {/*  </Col>*/}
        {/*</Row>*/}

        <Row className={'mt-3'}>
          <Col md={12}>
            <div className="card__title">
              <h4 className={'bold-text d-inline'}>CREDENTIALS</h4>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  download_file(
                    `${apiUrlInternal}/keyword_projects/${project_uid}/results/credentials_exposure/export`,
                    `export_${project_name}_credentials_exposure_${formatDateUTCtoYearMonthDayTimeFile(moment())}.csv`,
                    accessToken,
                    60000,
                    'Creating the CSV file, it can take few minutes'
                  );
                }}
                outline
                color={'success'}
                className={'float-right py-1 mr-0'}
              >
                Download
              </Button>
              <Button
                onClick={() => {
                  setBtnDisabled(true);
                  mutationSetAllCredentialsExpSolved.mutate(project_uid);
                }}
                className={'float-right py-1 mr-2'}
                disabled={btnDisabled}
              >
                Mark All As Solved
              </Button>
              <Button
                onClick={() => {
                  setBtnDisabled(true);
                  mutationSetCredentialsExpSolved.mutate({
                    project_uid: project_uid,
                    uids: uidsSelected
                  });
                }}
                className={'float-right py-1 mr-2'}
                disabled={btnDisabled || btnSelectDisabled}
              >
                Mark Selected As Solved
              </Button>
            </div>

            <CredentialTableWithPagination
              extraColumnsAfter={[
                {Header: 'Keyword', accessor: 'keyword.value'},
              ]}
              extraColumnsBefore={[
                {
                  Header: 'Status', accessor: 'solved',
                  Cell: ({value, row}) => {
                    const label = value ?
                      <p className={'d-inline'} style={{color: '#4ce1b6'}}>Solved</p> :
                      <p className={'d-inline'} style={{color: '#ff4861'}}>New Item</p>;

                    const index = row.index;
                    const uid = row.original.uid;

                    return (
                      <>
                        {
                          !value &&
                          <RegisteredCheckBoxField
                            name={`cred.${index}`}
                            onChange={() => {
                              if (watch(`cred.${index}`) && !uidsSelected.includes(uid)) {
                                setUidsSelected(uidsSelected.concat([uid]));
                              } else if (!watch(`cred.${index}`) && uidsSelected.includes(uid)) {
                                setUidsSelected(uidsSelected.filter(val => val !== uid));
                              }
                            }}
                            value={watch(`cred.${index}`)}
                            defaultChecked={false}
                            label={''}
                            register={register}
                            style={{display: 'inline'}}
                            className={'d-inline'}
                          />
                        }
                        {label}
                      </>
                    )
                  }
                },
              ]}
              totalItems={count}
              project_uid={project_uid}
              customSort={true}
            />
          </Col>
        </Row>

      </CardBody>
    </Card>
  )
}


CredentialExposure.propTypes = {
  project_uid: PropTypes.string.isRequired
}


export default CredentialExposure;
